<template>
  <v-container>
    <v-layout justify-center>
      <v-flex>
        <v-card class="profile">
          <v-app-bar dark color="primary">
            <h3>{{ $t('profile.yourProfileData') }}</h3>
            <v-spacer></v-spacer>
            <v-btn
              :color="iconColor"
              icon
              to="/edit-profile"
            >
              <icon
                default-icon-name="mdiAccountEdit"
                :size="24"
                :color="iconColor"
                :label="$t('profile.editProfile')"
                role="img"
              />
            </v-btn>
          </v-app-bar>
          <v-list v-if="hasFieldsComponent">
            <template v-for="(field, index) in fieldsInFieldsComponent">
              <span :key="index">
                <v-list-item class="py-2">
                  <v-list-item-title class="field-item">
                    {{ field.label }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="field-item text-right">
                    {{ field.value }}
                  </v-list-item-subtitle>
                </v-list-item>
                <v-divider v-if="index < fieldsInFieldsComponent.length - 1" />
              </span>
            </template>
          </v-list>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import {mapGetters} from 'vuex'
import {getCountryInfo} from '@/locales/i18n'
import {mdiAccountEdit} from '@mdi/js'
import Icon from '@/components/ui/Icon'

export default {
  components: {
    Icon,
  },
  data() {
    return {
      country: null,
      icons: {mdiAccountEdit}
    }
  },
  computed: {
    ...mapGetters([
      'authSettings',
      'navigationSettings',
      'user',
      'customUserFields',
      'webviewSettings',
    ]),
    components() {
      return _.get(
        this.navigationSettings, 'screens.materialAccountScreen.components', []
      )
    },
    fieldsComponent() {
      return this.components.find((component) => {
        return component.name === 'FieldsComponent' || null
      })
    },
    hasFieldsComponent() {
      return !_.isEmpty(this.fieldsComponent)
    },
    fieldsInFieldsComponent() {
      const fields = _.get(this.fieldsComponent, 'props.fields', [])
      const fieldsToRender = []

      fields.forEach((field) => {
        const format = _.get(field, 'format', '')
        const requirements = _.get(field, 'requirements', [])
        let label = field.label || ''
        let value = format

        if (!format) {
          return
        }

        if (!this.isSatisfyingRequirements(requirements)) {
          return
        }

        const placeholdersToReplace = format.match(/\{(.+?)\}/g) || []

        if (_.isEmpty(placeholdersToReplace)) {
          return
        }

        const placeholdersPayload = placeholdersToReplace.map((placeholder) => {
          return ({
            placeholder,
            payload: placeholder.replace(/[{}]/g, ''),
          })
        })

        const placeholdersValue = placeholdersPayload.map((placeholderObject) => {
          const placeholderValue =
            this.resolvePayloadValue(placeholderObject.payload)

          return {
            ...placeholderObject,
            placeholderValue,
          }
        })

        placeholdersValue.forEach((placeholderObject) => {
          const {placeholder, placeholderValue} = placeholderObject

          if (!placeholderValue) {
            value = ''
          }

          value = value ? value.replace(placeholder, placeholderValue) : ''
        })

        if (!label && placeholdersPayload.length === 1) {
          const payload = placeholdersPayload[0].payload
          label = this.resolveFieldLabel(payload)
        }

        if (!value) {
          return
        }

        fieldsToRender.push({
          label,
          value,
        })
      })

      return fieldsToRender
    },
    customUserFieldsToShow() {
      if (this.customUserFields) {
        const userFields = _.get(this.user, 'custom_fields', [])

        return this.customUserFields.map((field) => {
          let value = _.get(
            userFields.filter((f) => {
              return f.key === field.key
            })[0],
            'value',
            _.get(field, 'metadata.default')
          )
          let identifier = value

          if (field.type === 'date') {
            value = this.parseCustomUserFieldDate(value)
          }

          if (field.type === 'location') {
            value = _.get(value, 'name', '')
          }

          if (field.type === 'select') {
            let choice = {}

            if (value !== undefined) {
              [choice] = field.metadata.choices.filter((c) => {
                return c.value === value
              })
            } else {
              [choice] = field.metadata.choices.filter((c) => {
                return _.has(c, 'default')
              })
            }

            if (!_.isEmpty(choice)) {
              value = choice.title
              identifier = choice.value
            }
          }

          if (
            !!_.get(field, 'show_in_app', false)
              && (!_.isEmpty(value) || _.isBoolean(value))
              && value !== ''
          ) {
            let userFieldValue = ''
            let translations = ''

            if (_.isBoolean(value)) {
              if (value) {
                translations = this.$t('profile.booleanCustomUserFieldTrue')
              } else {
                translations = this.$t('profile.booleanCustomUserFieldFalse')
              }
              userFieldValue = translations
            } else {
              userFieldValue = `${value}`
            }

            return {
              key: field.key,
              name: field.name,
              value: userFieldValue,
              identifier,
            }
          }

          return null
        }).filter((field) => field)
      }

      return []
    },
    iconColor() {
      return _.get(this.webviewSettings, 'theme.bar_headers.icon_color') || _.get(this.webviewSettings, 'theme.navigation_bar.icon_color')
    },
  },
  async created() {
    await this.getCountryInfo()
  },
  methods: {
    async getCountryInfo() {
      try {
        const countryCode = _.get(this.user, 'address.country', '')
        this.country = await getCountryInfo(countryCode, this.$i18n.locale)
      } catch (error) {
        console.log(error)
      }
    },
    isSatisfyingRequirements(requirements) {
      if (!_.isArray(requirements) || _.isEmpty(requirements)) {
        return true
      }

      return !requirements.some((requirement) => {
        const key = _.get(requirement, 'key', '')
        const alowedValues = _.get(requirement, 'allowed_values', [])

        if (!key) {
          return true
        }

        if (!_.isArray(alowedValues)) {
          return true
        }

        const value = this.resolvePayloadValue(key, 'identifier')

        return !alowedValues.some((alowedValue) => {
          return alowedValue === value
        })
      })
    },
    resolvePayloadValue(payload, lookupField = 'value') {
      const payloadSegments = payload.split('.')

      // Get value from user
      if (payloadSegments[0] === 'user') {
        payloadSegments.shift()

        const resolvedValue = _.get(
          this.user, payloadSegments.join('.'), ''
        )

        if (payloadSegments[0] === 'birthday') {
          return moment(resolvedValue, 'DD.MM.YYYY').format('DD.MM.YYYY')
        }

        return resolvedValue
      }

      // Get value from custom user fields
      if (payloadSegments[0] === 'custom_user_field') {
        const lookupKey = payloadSegments[payloadSegments.length - 1]

        const customUserField = this.customUserFieldsToShow.find((customField) => {
          return customField.key === lookupKey
        })

        return _.get(customUserField, lookupField, '')
      }

      // Resolve payload from the state or return emptry string
      return _.get(this, payload) || ''
    },
    resolveFieldLabel(payload) {
      const payloadSegments = payload.split('.')

      if (payloadSegments[0] === 'user') {
        return this.getTranslatedLabelForField(
          payloadSegments[payloadSegments.length - 1]
        )
      }

      if (payloadSegments[0] === 'custom_user_field') {
        const lookupKey = payloadSegments[payloadSegments.length - 1]

        const customUserField = this.customUserFieldsToShow.find(
          (customField) => {
            return customField.key === lookupKey
          }
        )
        return _.get(customUserField, 'name', '')
      }

      return this.getTranslatedLabelForField(payloadSegments[0])
    },
    parseCustomUserFieldDate(dateString) {
      if (!_.isEmpty(dateString)) {
        const customDate = moment(dateString, 'YYYY-MM-DD')

        if (moment.isMoment(customDate) && customDate.isValid()) {
          return customDate.format('DD.MM.YYYY')
        }
      }

      return null
    },
    getTranslatedLabelForField(field) {
      switch (field) {
        case 'first_name':
          return this.$t('profile.firstNameLabel')
        case 'last_name':
          return this.$t('profile.lastNameLabel')
        case 'email':
          return this.$t('profile.emailLabel')
        case 'phone_number':
          return this.$t('profile.phoneNumberLabel')
        case 'birthday':
          return this.$t('profile.birthdayLabel')
        case 'gender':
          return this.$t('profile.genderLabel')
        case 'customer_id':
          return this.$t('profile.customerIdLabel')
        case 'address':
          return this.$t('profile.addressLabel')
        case 'street':
          return this.$t('profile.streetLabel')
        case 'city_code':
          return this.$t('profile.cityCodeLabel')
        case 'city':
          return this.$t('profile.cityLabel')
        case 'state':
          return this.$t('profile.stateLabel')
        case 'street_number':
          return this.$t('profile.streetNumberLabel')
        case 'country':
          return this.$t('profile.countryLabel')
        default:
          return ''
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.profile {
  max-width: 720px;
  margin: 0 auto;
}

.field-item {
  font-size: 1rem;

  @media (min-width: 600px) {
    font-size: 1.125rem;
  }
}
</style>
